import {
    ACTION_TYPES,
    ITEM_LOCATION_TYPES,
    ITEM_TOP_LEVEL,
    SPECIAL_ACTION_IDS,
} from '@/roadmaps/constants/roadmaps.constants';
import {
    getItemLocationTypeByLevel,
    getPendingForbiddenAction,
    getPendingResolveActionsForChildren,
    getPendingResolveActionsForNestedItems,
    getPendingResolveActionsForSiblingsForMoveToBacklog,
    getPendingResolveActionsForUnNesting,
} from '@/roadmaps/helpers/interactions/roadmaps-common-interactions.helper';
/* eslint-disable no-restricted-imports */
import store from '@/vuex/store';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

export function getPendingDragActions (rootSource, newRootSourceLocation) {
    const dragAction = {
        id: rootSource.id,
        type: ACTION_TYPES.DRAG,
        updates: newRootSourceLocation,
        targetItem: null,
        preview: true,
    };

    const resolveActions = [];

    const unNestingResolveActions = getPendingResolveActionsForUnNesting(rootSource, newRootSourceLocation.parentId);
    resolveActions.push(...unNestingResolveActions);

    const isChangingSwimlaneColumnContainer =
        rootSource.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key &&
        (newRootSourceLocation.parentId !== rootSource.parentId ||
            newRootSourceLocation.columnId !== rootSource.columnId);
    if (isChangingSwimlaneColumnContainer) {
        const previousSwimlaneColumnContainerResolves = getPendingResolveActionsForColumnItems(
            rootSource.parentId,
            rootSource.columnId,
        );
        resolveActions.push(...previousSwimlaneColumnContainerResolves);
    }

    const isBacklogParentWithChildren =
        rootSource.parentType === ITEM_LOCATION_TYPES.BACKLOG.key &&
        Object.keys(store.getters['roadmaps/items/itemsByParentId'](rootSource.id)).length > 0;

    if (isBacklogParentWithChildren) {
        const backlogChildResolves = getPendingResolveActionsForBacklogChildren(rootSource);
        resolveActions.push(...backlogChildResolves);
    }

    const rootSourceWithUpdates = Object.assign({}, rootSource, newRootSourceLocation);
    const newSwimlaneColumnContainerResolves = getPendingResolveActionsForColumnItems(
        newRootSourceLocation.parentId,
        newRootSourceLocation.columnId,
        rootSourceWithUpdates,
    );
    resolveActions.push(...newSwimlaneColumnContainerResolves);

    return keyBy([dragAction, ...resolveActions], 'id');
}

export function getPendingGroupActions (rootSource, target, row) {
    const sourceLevel = ITEM_LOCATION_TYPES[rootSource.type].level;
    const targetLevel = ITEM_LOCATION_TYPES[target.type].level;

    const canPromote = sourceLevel === targetLevel && targetLevel !== ITEM_TOP_LEVEL;
    const canNest = sourceLevel > targetLevel;
    if (!canPromote && !canNest) {
        return getPendingForbiddenAction(rootSource, target);
    }

    const resolveActions = [];

    const unNestingResolveActions = getPendingResolveActionsForUnNesting(rootSource, target.id);
    resolveActions.push(...unNestingResolveActions);

    const isChangingSwimlaneColumnContainer =
        rootSource.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key &&
        (target.parentId !== rootSource.parentId || target.columnId !== rootSource.columnId);
    if (isChangingSwimlaneColumnContainer) {
        const previousSwimlaneColumnContainerResolves = getPendingResolveActionsForColumnItems(
            rootSource.parentId,
            rootSource.columnId,
        );
        resolveActions.push(...previousSwimlaneColumnContainerResolves);
    }

    if (canPromote) {
        const promoteActions = getPendingPromoteActions(rootSource, target);

        const isNotChangingSwimlaneColumnContainer =
            rootSource.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key &&
            (target.parentId === rootSource.parentId || target.columnId === rootSource.columnId);
        if (isNotChangingSwimlaneColumnContainer) {
            const sameSwimlaneColumnContainerResolves = getPendingResolveActionsForColumnItems(
                target.parentId,
                target.columnId,
            );
            resolveActions.push(...sameSwimlaneColumnContainerResolves);
        }

        return keyBy([...resolveActions, ...promoteActions], 'id');
    }

    if (canNest) {
        const nestAction = getPendingNestAction(rootSource, target, row);

        const childToAdd = Object.assign({}, rootSource, nestAction.updates);
        const newSiblingResolves = getPendingResolveActionsForChildren(target.id, childToAdd);
        resolveActions.push(...newSiblingResolves);

        return keyBy([...resolveActions, nestAction], 'id');
    }
}

export function getPendingCreateItemActions (rootSource, newRootSourceLocation) {
    const createAction = {
        id: rootSource.id,
        type: ACTION_TYPES.CREATE_ITEM,
        updates: newRootSourceLocation,
        targetItem: null,
        preview: true,
    };

    const rootSourceWithUpdates = Object.assign({}, rootSource, newRootSourceLocation);
    const resolveActions = getPendingResolveActionsForColumnItems(
        newRootSourceLocation.parentId,
        newRootSourceLocation.columnId,
        rootSourceWithUpdates,
    );

    return keyBy([createAction, ...resolveActions], 'id');
}

function getPendingPromoteActions (rootSource, target) {
    const sourceLevel = ITEM_LOCATION_TYPES[rootSource.type].level;
    const parentType = getItemLocationTypeByLevel(sourceLevel - 1);

    const isSourceInSameContainerAsTarget =
        rootSource.parentId === target.parentId && rootSource.columnId === target.columnId;
    const hasTargetMovedUp = isSourceInSameContainerAsTarget && rootSource.row < target.row;
    const promoteParentRow = hasTargetMovedUp ? target.row - 1 : target.row;

    const parentAction = {
        id: SPECIAL_ACTION_IDS.PROMOTE_PARENT,
        type: ACTION_TYPES.PROMOTE_PARENT,
        updates: {
            parentId: target.parentId,
            parentType: target.parentType,
            columnId: target.columnId,
            type: parentType,
            row: promoteParentRow,
            onBacklog: false,
            isExpanded: false,
        },
        targetItem: null,
        preview: true,
    };

    const childSourceAction = {
        id: rootSource.id,
        type: ACTION_TYPES.PROMOTE_CHILD_SOURCE,
        updates: {
            parentType,
            columnId: '',
            row: 1,
        },
        targetItem: target,
        preview: true,
    };

    if (rootSource.onBacklog) {
        childSourceAction.updates.onBacklog = false;
    }

    const childTargetAction = {
        id: target.id,
        type: ACTION_TYPES.PROMOTE_CHILD_TARGET,
        updates: {
            parentType,
            columnId: '',
            row: 0,
        },
        targetItem: null,
        preview: true,
    };

    return [parentAction, childSourceAction, childTargetAction];
}

function getPendingNestAction (rootSource, target, targetNestedRow) {
    const isSourceInSameContainerAsTarget = rootSource.parentId === target.id;
    const hasTargetMovedUp = isSourceInSameContainerAsTarget && rootSource.row < targetNestedRow;
    const newRow = hasTargetMovedUp ? targetNestedRow - 1 : targetNestedRow;

    const nestAction = {
        id: rootSource.id,
        type: ACTION_TYPES.NEST,
        updates: {
            parentId: target.id,
            parentType: target.type,
            columnId: '',
            row: newRow,
        },
        targetItem: target,
        preview: true,
    };

    if (rootSource.onBacklog) {
        nestAction.updates.onBacklog = false;
    }

    return nestAction;
}

function getPendingResolveActionsForColumnItems (parentId, columnId, itemToAdd) {
    const columnItems = sortBy(
        store.getters['roadmaps/items/itemsByParentIdAndColumnIdWithoutDraggedItem'](parentId, columnId),
        'row',
    );

    if (itemToAdd) {
        columnItems.splice(itemToAdd.row, 0, itemToAdd);
    }

    return getPendingResolveActionsForNestedItems(columnItems);
}

function getPendingResolveActionsForBacklogChildren (parent) {
    const childItems = store.getters['roadmaps/items/itemsByParentId'](parent.id);

    return map(childItems, (childItem) => {
        return {
            id: childItem.id,
            type: ACTION_TYPES.RESOLVE,
            updates: {
                onBacklog: false,
            },
            targetItem: null,
            preview: true,
        };
    });
}

export function getPendingMoveToBacklogActions (rootSource) {
    const moveToBacklogAction = {
        id: rootSource.id,
        type: ACTION_TYPES.MOVE_TO_BACKLOG,
        updates: {
            parentId: '',
            parentType: ITEM_LOCATION_TYPES.BACKLOG.key,
            onBacklog: true,
            columnId: '',
            row: null,
        },
        targetItem: null,
        preview: true,
    };

    const resolveActions = getPendingResolveActionsForMoveToBacklog(rootSource);

    return keyBy([moveToBacklogAction, ...resolveActions], 'id');
}

function getPendingResolveActionsForMoveToBacklog (rootSource) {
    const resolveActions = [];

    const childItems = store.getters['roadmaps/items/itemsByParentId'](rootSource.id);
    if (childItems.length > 0) {
        const childResolves = map(childItems, (childItem) => {
            return {
                id: childItem.id,
                type: ACTION_TYPES.RESOLVE,
                updates: {
                    onBacklog: true,
                    columnId: '',
                },
                targetItem: null,
                preview: true,
            };
        });

        resolveActions.push(...childResolves);
    }

    const previousSiblingResolveActions = getPendingResolveActionsForSiblingsForMoveToBacklog(rootSource);
    resolveActions.push(...previousSiblingResolveActions);

    return resolveActions;
}
